"use client";

import { ArrowDownToLine, ArrowUpToLine, Plus, Scissors, Trash2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";
import { Kbd, KbdGroup } from "@/components/ui/kbd";

/**
 * The cue editing actions — Add / Split / Merge / Delete as a segmented Button
 * Group, plus the keyboard-shortcut hint. Presentational; the parent owns the
 * cue state and the enabled flags.
 *
 * `compact` renders icon-only buttons with tooltip labels and no shortcut hint
 * — for narrow rails (the composition editor's subtitle pane), where the
 * labeled group overflows sideways and the sub editor's cue shortcuts don't
 * apply anyway.
 */
export function CueActionRow({
  busy,
  canAdd = true,
  canSplit,
  canMerge,
  canDelete,
  canMergePrevious,
  compact,
  onAdd,
  onSplit,
  onMerge,
  onMergePrevious,
  onDelete,
}: {
  busy: boolean;
  /** Adding cues can be structurally impossible (single-cue label assets). */
  canAdd?: boolean;
  canSplit: boolean;
  canMerge: boolean;
  canDelete: boolean;
  canMergePrevious?: boolean;
  compact?: boolean;
  onAdd: () => void;
  onSplit: () => void;
  onMerge: () => void;
  onMergePrevious?: () => void;
  onDelete: () => void;
}) {
  const t = useTranslations("mediaTools.editor");

  if (compact) {
    const iconButton = (
      label: string,
      icon: React.ReactNode,
      onClick: () => void,
      disabled: boolean
    ) => (
      <Button
        variant="outline"
        size="sm"
        className="w-9 px-0"
        aria-label={label}
        title={label}
        onClick={onClick}
        disabled={disabled}
      >
        {icon}
      </Button>
    );
    return (
      <ButtonGroup>
        {iconButton(t("actions.add"), <Plus className="h-4 w-4" />, onAdd, busy || !canAdd)}
        {iconButton(
          t("actions.split"),
          <Scissors className="h-4 w-4" />,
          onSplit,
          busy || !canSplit
        )}
        {onMergePrevious &&
          iconButton(
            t("actions.mergePrevious"),
            <ArrowUpToLine className="h-4 w-4" />,
            onMergePrevious,
            busy || !canMergePrevious
          )}
        {iconButton(
          t("actions.mergeNext"),
          <ArrowDownToLine className="h-4 w-4" />,
          onMerge,
          busy || !canMerge
        )}
        {iconButton(
          t("actions.delete"),
          <Trash2 className="h-4 w-4" />,
          onDelete,
          busy || !canDelete
        )}
      </ButtonGroup>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <ButtonGroup>
        <Button variant="outline" size="sm" onClick={onAdd} disabled={busy || !canAdd}>
          <Plus className="mr-1.5 h-4 w-4" /> {t("actions.add")}
        </Button>
        <Button variant="outline" size="sm" onClick={onSplit} disabled={busy || !canSplit}>
          <Scissors className="mr-1.5 h-4 w-4" /> {t("actions.split")}
        </Button>
        {onMergePrevious && (
          <Button
            variant="outline"
            size="sm"
            onClick={onMergePrevious}
            disabled={busy || !canMergePrevious}
          >
            {t("actions.mergePrevious")}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={onMerge} disabled={busy || !canMerge}>
          {t("actions.mergeNext")}
        </Button>
        <Button variant="outline" size="sm" onClick={onDelete} disabled={busy || !canDelete}>
          <Trash2 className="mr-1.5 h-4 w-4" /> {t("actions.delete")}
        </Button>
      </ButtonGroup>
      <span className="ml-1 inline-flex items-center gap-1 text-foreground/40 text-xs">
        {t("actions.cueActionHint")}
        <KbdGroup>
          <Kbd>S</Kbd>
          <Kbd>M</Kbd>
          <Kbd>⌫</Kbd>
        </KbdGroup>
        · {t("actions.splitHint")}
      </span>
    </div>
  );
}
